import { useEffect, useRef } from 'react';
import { pareceLecturaScanner } from '../helpers/lectorCedula';

/* ─────────────────────────────────────────────────────────────────
   useLectorCedula
   Detecta lecturas de una pistola de código de barras.

   La pistola se comporta como un teclado: "escribe" el contenido muy
   rápido (una tecla cada pocos milisegundos) y termina con Enter o
   Tab. Aquí se acumulan esas pulsaciones y, cuando hay una pausa
   (silencioMs) o llega Enter/Tab, se entrega la lectura completa con
   `onLectura(cadenaCruda)`.

   Para no confundirse con alguien escribiendo a mano:
   - se ignora lo que se teclea dentro de un input/textarea/select,
   - una pausa larga descarta lo acumulado,
   - si la lectura trae sólo dígitos se exige velocidad de pistola.
   ───────────────────────────────────────────────────────────────── */
export const useLectorCedula = ({
    activo = true,
    onLectura,
    silencioMs = 80,
    msMaximoPorTecla = 60,
    pausaMs = 250,
} = {}) => {
    const buffer = useRef('');
    const temporizador = useRef(null);
    const primeraTecla = useRef(0);
    const ultimaTecla = useRef(0);
    const teclas = useRef(0);

    // Siempre se llama a la última versión del callback sin reconectar
    // el listener en cada render
    const onLecturaRef = useRef(onLectura);
    onLecturaRef.current = onLectura;

    useEffect(() => {
        if (!activo) return undefined;

        const limpiarTemporizador = () => {
            if (temporizador.current) {
                clearTimeout(temporizador.current);
                temporizador.current = null;
            }
        };

        const reiniciar = () => {
            limpiarTemporizador();
            buffer.current = '';
            teclas.current = 0;
            primeraTecla.current = 0;
            ultimaTecla.current = 0;
        };

        const procesar = () => {
            limpiarTemporizador();
            const crudo = buffer.current;
            const total = teclas.current;
            const inicio = primeraTecla.current;
            const fin = ultimaTecla.current;
            reiniciar();
            if (!crudo) return;

            if (!pareceLecturaScanner(crudo)) return;

            // Sólo dígitos: exigir velocidad de pistola para no confundir
            // con alguien tecleando en un teclado físico
            const soloDigitos = /^\d+$/.test(crudo);
            const promedio = total > 1 ? (fin - inicio) / (total - 1) : 0;
            if (soloDigitos && promedio > msMaximoPorTecla) return;

            if (onLecturaRef.current) onLecturaRef.current(crudo);
        };

        const esCampoEditable = (el) => {
            if (!el || !el.tagName) return false;
            const tag = el.tagName.toLowerCase();
            return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable === true;
        };

        const onKeyDown = (evento) => {
            if (evento.ctrlKey || evento.altKey || evento.metaKey) return;
            if (esCampoEditable(evento.target)) return;

            if (evento.key === 'Enter' || evento.key === 'Tab') {
                if (buffer.current) {
                    evento.preventDefault();
                    procesar();
                }
                return;
            }

            if (evento.key.length !== 1) {
                // Backspace / Escape cancelan la lectura en curso
                if (evento.key === 'Backspace' || evento.key === 'Escape') reiniciar();
                return;
            }

            const ahora = performance.now();
            if (buffer.current && ahora - ultimaTecla.current > pausaMs) {
                // Pausa larga: lo anterior no era una lectura
                reiniciar();
            }

            if (!buffer.current) primeraTecla.current = ahora;
            buffer.current += evento.key;
            teclas.current += 1;
            ultimaTecla.current = ahora;

            limpiarTemporizador();
            temporizador.current = setTimeout(procesar, silencioMs);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            limpiarTemporizador();
        };
    }, [activo, silencioMs, msMaximoPorTecla, pausaMs]);
};

export default useLectorCedula;
